import { useCallback, useEffect, useRef, useState } from "react";

import { addWaitlistEmail, deleteWaitlistSignup, fetchWaitlistSignups } from "./api";
import type { WaitlistSignup } from "./types";

const POLL_INTERVAL_MS = 30_000;

export type SortDirection = "asc" | "desc";

export interface UseWaitlistReturn {
  signups: WaitlistSignup[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
  addEmail: (email: string) => Promise<{ success: boolean; message: string }>;
  deleteSignup: (id: string) => Promise<void>;
  sortDirection: SortDirection;
  toggleSort: () => void;
  lastRefreshedAt: Date | null;
}

export function useWaitlist(): UseWaitlistReturn {
  const [signups, setSignups] = useState<WaitlistSignup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadSignups = useCallback(async () => {
    try {
      const data = await fetchWaitlistSignups();
      setSignups(data);
      setError(null);
      setLastRefreshedAt(new Date());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load signups");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refresh = useCallback(() => {
    setIsLoading(true);
    void loadSignups();
  }, [loadSignups]);

  // Initial load + polling
  useEffect(() => {
    void loadSignups();

    intervalRef.current = setInterval(() => {
      void loadSignups();
    }, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
      }
    };
  }, [loadSignups]);

  // Pause polling when tab is hidden
  useEffect(() => {
    function handleVisibilityChange(): void {
      if (document.hidden) {
        if (intervalRef.current !== null) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      } else {
        void loadSignups();
        if (intervalRef.current !== null) {
          clearInterval(intervalRef.current);
        }
        intervalRef.current = setInterval(() => {
          void loadSignups();
        }, POLL_INTERVAL_MS);
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [loadSignups]);

  const addEmail = useCallback(
    async (email: string): Promise<{ success: boolean; message: string }> => {
      try {
        const result = await addWaitlistEmail(email);
        if (result.alreadyExists) {
          return { success: true, message: "Email already on the waitlist" };
        }
        void loadSignups();
        return { success: true, message: "Email added successfully" };
      } catch (e) {
        return {
          success: false,
          message: e instanceof Error ? e.message : "Failed to add email",
        };
      }
    },
    [loadSignups]
  );

  const deleteSignup = useCallback(
    async (id: string): Promise<void> => {
      await deleteWaitlistSignup(id);
      void loadSignups();
    },
    [loadSignups]
  );

  const toggleSort = useCallback(() => {
    setSortDirection((prev) => (prev === "desc" ? "asc" : "desc"));
  }, []);

  const sortedSignups = [...signups].sort((a, b) => {
    const comparison = a.createdAt.localeCompare(b.createdAt);
    return sortDirection === "desc" ? -comparison : comparison;
  });

  return {
    signups: sortedSignups,
    isLoading,
    error,
    refresh,
    addEmail,
    deleteSignup,
    sortDirection,
    toggleSort,
    lastRefreshedAt,
  };
}
